(() => {
  'use strict';

  const script = document.currentScript;
  if (!script || window.AONSKProductToolsLoader) return;
  window.AONSKProductToolsLoader = true;

  const loaded = new Map();
  const sources = {
    details: script.dataset.detailsSrc,
    soil: script.dataset.soilSrc,
    planner: script.dataset.plannerSrc,
    specialized: script.dataset.specializedSrc
  };

  const selectors = {
    details: '[data-bcm7-accordion]',
    soil: '[data-bcm7-soil]',
    planner: '[data-bcm7-planner]',
    specialized: '[data-bcm8-bean-planner], [data-bcm8-corn-planner], [data-bcm8-perennial-check], [data-bcm8-sunflower-planner]'
  };

  function load(key) {
    const src = sources[key];
    if (!src) return Promise.resolve();
    if (loaded.has(key)) return loaded.get(key);

    const promise = new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = src;
      el.async = true;
      el.dataset.aonskProductTool = key;
      el.addEventListener('load', resolve, { once: true });
      el.addEventListener('error', reject, { once: true });
      document.head.appendChild(el);
    });
    loaded.set(key, promise);
    return promise;
  }

  function keyForTarget(target) {
    if (!(target instanceof Element)) return null;
    for (const [key, selector] of Object.entries(selectors)) {
      if (target.closest(selector)) return key;
    }
    return null;
  }

  function warmOnUse(event) {
    const key = keyForTarget(event.target);
    if (!key || key === 'details') return;
    load(key).catch(() => {});
  }

  function loadToolsWithin(container) {
    if (!(container instanceof Element)) return;
    Object.entries(selectors).forEach(([key, selector]) => {
      if (key === 'details') return;
      if (container.matches(selector) || container.querySelector(selector)) {
        load(key).catch(() => {});
      }
    });
  }

  function observeTools(root = document) {
    const entries = Object.entries(selectors).filter(([key]) => key !== 'details');

    if (!('IntersectionObserver' in window)) {
      const schedule = window.requestIdleCallback || ((callback) => setTimeout(callback, 1800));
      schedule(() => {
        entries.forEach(([key, selector]) => {
          if (root.querySelector(selector)) load(key).catch(() => {});
        });
      }, { timeout: 3500 });
      return;
    }

    const observer = new IntersectionObserver((observations) => {
      observations.forEach((observation) => {
        if (!observation.isIntersecting) return;
        const key = observation.target.dataset.aonskToolKey;
        observer.unobserve(observation.target);
        if (key) load(key).catch(() => {});
      });
    }, { rootMargin: '700px 0px' });

    entries.forEach(([key, selector]) => {
      root.querySelectorAll(selector).forEach((element) => {
        if (element.dataset.aonskToolObserved === 'true') return;
        element.dataset.aonskToolObserved = 'true';
        element.dataset.aonskToolKey = key;
        observer.observe(element);
      });
    });
  }

  // The product accordion is lightweight and supports review access, so load it
  // as soon as its markup exists. Calculators load before they approach the
  // viewport or on the first touch, pointer, or keyboard focus.
  if (document.querySelector(selectors.details)) load('details').catch(() => {});
  observeTools(document);

  document.addEventListener('pointerover', warmOnUse, { passive: true, capture: true });
  document.addEventListener('pointerdown', warmOnUse, { passive: true, capture: true });
  document.addEventListener('touchstart', warmOnUse, { passive: true, capture: true });
  document.addEventListener('focusin', warmOnUse, { passive: true, capture: true });

  // The calculators live inside a closed Garden Tools disclosure on first load.
  // A customer opening that drawer should never wait for the observer to notice
  // hidden descendants, so warm every tool contained in the opened drawer.
  document.addEventListener('toggle', (event) => {
    const drawer = event.target;
    if (!(drawer instanceof HTMLDetailsElement)) return;
    if (!drawer.open || !drawer.id.startsWith('seed-garden-tools-')) return;
    loadToolsWithin(drawer);
  }, true);

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector(selectors.details)) load('details').catch(() => {});
    observeTools(event.target);
  });
})();
